// src/lib/config/websocketConfig.ts

import { API_CONFIG } from './apiConfig';
import type { WebSocketConfig } from '$lib/types/websocket';

/**
 * Generate a UUID v4 for uniquely identifying WebSocket connections
 */
function generateUUID(): string {
	if (typeof crypto !== 'undefined' && crypto.randomUUID) {
		return crypto.randomUUID();
	}

	// Fallback for older browsers
	return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
		const r = (Math.random() * 16) | 0;
		const v = c === 'x' ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
}

/**
 * Get the WebSocket URL using the API configuration
 * Uses the same domain as the API base URL for consistency
 * Includes the /connect/{client_id} path required by the streamlined backend
 */
/**
 * Read the active scope from sessionStorage (the canonical FE source, set by
 * `scope.state.svelte.ts`). Returns `'own'` when no scope is stored.
 *
 * We read sessionStorage rather than importing the scope state module to avoid
 * a circular import at module-init time (WS config loads very early; scope
 * state imports the WS actions module to rotate subscriptions).
 *
 * Always sent on connect so the BE can run the same permission-checked
 * resolution path REST uses — scope leak fix per spec 2026-05-11.
 */
function getActiveScope(): string {
	if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
		return 'own';
	}
	return sessionStorage.getItem('amos:scope') ?? 'own';
}

function getWebSocketUrl(): string {
	// Check if we're in a browser environment first
	const isBrowser = typeof window !== 'undefined';

	if (!isBrowser) {
		// During SSR, return a placeholder - it won't be used until hydration
		return 'ws://placeholder-during-ssr';
	}

	// Generate a client ID - use user ID if logged in, otherwise generate a random UUID
	const clientId =
		typeof localStorage !== 'undefined' && localStorage.getItem('user_id')
			? localStorage.getItem('user_id')
			: generateUUID(); // Generate a unique UUID instead of static '1'

	const scopeQs = `?scope_id=${encodeURIComponent(getActiveScope())}`;

	try {
		// Extract API domain from the base URL
		const apiUrlObj = new URL(API_CONFIG.BASE_URL);
		const apiDomain = apiUrlObj.host;

		// Use same protocol (ws/wss) based on API protocol (http/https)
		const wsProtocol = apiUrlObj.protocol === 'https:' ? 'wss:' : 'ws:';

		// Construct WebSocket URL using the API domain and proper path format
		// Using new streamlined WebSocket endpoint with v1 prefix
		return `${wsProtocol}//${apiDomain}/api/v1/ws/connect/${clientId}${scopeQs}`;
	} catch (error) {
		console.warn('Failed to parse API domain, using fallback WebSocket URL', error);

		// Fallback - use the API domain directly with v1 prefix
		if (API_CONFIG.BASE_URL.includes('aspenpower-amos.com')) {
			return `wss://api.aspenpower-amos.com/api/v1/ws/connect/${clientId}${scopeQs}`;
		}

		// Last resort fallback using window location with v1 prefix
		if (typeof window !== 'undefined') {
			const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
			return `${protocol}//${window.location.host}/api/v1/ws/connect/${clientId}${scopeQs}`;
		}

		// Ultimate fallback with v1 prefix
		return `wss://api.aspenpower-amos.com/api/v1/ws/connect/${clientId}${scopeQs}`;
	}
}

/**
 * WebSocket configuration
 * Note: url uses a getter to avoid accessing API_CONFIG during module initialization (SSR compatibility)
 */
export const WEBSOCKET_CONFIG: WebSocketConfig = {
	get url() {
		return getWebSocketUrl();
	},
	reconnectDelay: 5000,
	heartbeatInterval: 30000,
	maxReconnectAttempts: 5
};

export default WEBSOCKET_CONFIG;
